import React, { useEffect, useState } from "react";
import CoverageStrip from "./CoverageStrip";
import {
  getDate,
  calculateCoverage,
  createMaterials,
  getMaterial,
} from "../../utils/reportUtils";
import { useNavigate, useOutletContext } from "react-router-dom";

const Materials = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [results, setResults] = useState([]);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { getReports } = useOutletContext();

  // India date, not UTC, so "today" is correct between 00:00 and 05:30 IST.
  const today = getDate(new Date());

  useEffect(() => {
    loadMaterials();
  }, []);

  const loadMaterials = async () => {
    setLoading(true);
    setError(null);

    try {
      // Same reports as the Dashboard, reused from its last load (loaded once if there is none yet).
      const { m, s, p, o, prd, alt, d, u } = await getReports(false);

      const mrpData = m.data || [];
      const stockData = s.data || [];
      const prData = p.data || [];
      const poData = o.data || [];
      const productionData = prd.data || [];

      setMaterials(
        createMaterials(mrpData, stockData, prData, poData, productionData),
      );
      setResults(
        calculateCoverage(
          mrpData,
          stockData,
          prData,
          poData,
          today,
          productionData,
          alt.data || [],
          d.data || [],
          u.data?.uploadDate || u.data?.createdDate,
          u.data?.uploadDate || u.data?.createdDate,
        ),
      );
    } catch (e) {
      setError(
        e.response?.data?.Exception ||
          e.response?.data?.message ||
          "Could not load materials.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getCoverageClass = (status) => {
    if (status === "COVERED") {
      return "b-ok";
    }

    if (status === "AT_RISK") {
      return "b-risk";
    }

    return "b-short";
  };

  const getCoverageText = (status) => {
    if (status === "COVERED") {
      return "Covered";
    }

    if (status === "AT_RISK") {
      return "At risk";
    }

    return "Short";
  };

  const filteredMaterials = materials.filter((material) => {
    const value =
      `${material.code} ${material.desc} ${material.materialGroup}`.toLowerCase();

    return value.includes(q.trim().toLowerCase());
  });

  if (loading) {
    return (
      <div className="page">
        <div className="empty">Loading materials...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="note err">
          <b>Couldn't load data.</b> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Materials</h1>
          <p>{filteredMaterials.length} materials in the active snapshot</p>
        </div>
      </div>

      <div className="ctl">
        <div className="search">
          <input
            placeholder="Search material, description or group..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <div className="tw">
          <table className="dt">
            <thead>
              <tr>
                <th>Material</th>
                <th>Description</th>
                <th>Group</th>
                <th>UOM</th>
                <th>Coverage</th>
                <th>10-week coverage</th>
                <th>Plant</th>
              </tr>
            </thead>

            <tbody>
              {filteredMaterials.length > 0 ? (
                filteredMaterials.map((material) => {
                  const result = getMaterial(results, material.key);

                  return (
                    <tr
                      key={material.key}
                      onClick={() => navigate(`../shortages/${material.key}`)}
                      className="clk"
                    >
                      <td className="mono">{material.code}</td>

                      <td>{material.desc}</td>

                      <td>{material.materialGroup}</td>

                      <td className="dim">{material.uom}</td>

                      <td>
                        {result && (
                          <span
                            className={`bg ${getCoverageClass(result.status)}`}
                          >
                            {getCoverageText(result.status)}
                          </span>
                        )}
                      </td>

                      <td>
                        {result && (
                          <CoverageStrip result={result} today={today} />
                        )}
                      </td>

                      <td className="dim">{material.plant}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="empty">
                    <strong>No materials found.</strong>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Materials;
